"use client"

import { Component, ErrorInfo, ReactNode } from 'react'
import { Button } from '@/components/ui/button'
import { AlertTriangle, RefreshCw, ArrowLeft, MessageSquare, Zap, Copy, RotateCcw, Home } from 'lucide-react'
import { reportError } from '@/lib/error-reporting'

interface ErrorBoundaryState {
  hasError: boolean
  error?: Error
  errorInfo?: ErrorInfo
  userFeedback: string
  showFeedback: boolean
  copySuccess: boolean
}

interface ErrorBoundaryProps {
  children: ReactNode
  fallback?: ReactNode
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { 
      hasError: false,
      userFeedback: '',
      showFeedback: false,
      copySuccess: false
    }
    this.handleKeyPress = this.handleKeyPress.bind(this)
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { 
      hasError: true, 
      error,
      userFeedback: '',
      showFeedback: false,
      copySuccess: false
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo)
    
    // Log to console for immediate debugging
    if (typeof window !== 'undefined') {
      console.error('🚨 Error Boundary:', {
        error: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        page: window.location.pathname
      })
    }

    // Report error to backend for monitoring and fixes
    reportError(error, errorInfo.componentStack || undefined, {
      page: typeof window !== 'undefined' ? window.location.pathname : undefined
    })
    
    this.setState({ errorInfo })
  }

  componentDidMount() {
    if (this.state.hasError) {
      document.addEventListener('keydown', this.handleKeyPress)
    }
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleKeyPress)
  }

  handleKeyPress = (event: KeyboardEvent) => {
    if (event.ctrlKey && event.shiftKey && event.key === 'R') {
      event.preventDefault()
      window.location.reload()
    } else if (event.key === 'Escape') {
      event.preventDefault()
      window.history.back()
    } else if (event.ctrlKey && event.key === 'h') {
      event.preventDefault()
      window.location.href = '/'
    }
  }

  handleCopyError = async () => {
    const errorText = `${this.state.error?.message}\n\nStack:\n${this.state.error?.stack}\n\nComponent Stack:\n${this.state.errorInfo?.componentStack}`
    try {
      await navigator.clipboard.writeText(errorText)
      this.setState({ copySuccess: true })
      setTimeout(() => this.setState({ copySuccess: false }), 2000)
    } catch (err) {
      console.warn('Failed to copy to clipboard')
    }
  }

  handleFeedbackSubmit = () => {
    if (this.state.userFeedback.trim()) {
      reportError(new Error(`User Feedback: ${this.state.userFeedback}`), undefined, {
        page: typeof window !== 'undefined' ? window.location.pathname : undefined
      })
      this.setState({ showFeedback: false, userFeedback: '' })
    }
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      return (
        <div className="min-h-screen bg-white flex items-center justify-center p-4">
          <div className="max-w-2xl w-full">
            {/* Main Error Card */}
            <div className="bg-[#facc15] border-4 border-black rounded-lg p-8 rotate-1 mb-6 hover:rotate-0 transition-transform duration-200">
              <div className="flex items-center gap-4 mb-6">
                <div className="bg-black border-2 border-black rounded-lg p-3">
                  <AlertTriangle className="h-8 w-8 text-[#facc15]" />
                </div>
                <div>
                  <h1 className="text-4xl font-black text-black uppercase tracking-tight">
                    OOPS!
                  </h1>
                  <p className="text-xl font-bold text-black">
                    Something broke
                  </p>
                </div>
              </div>
              
              <p className="text-lg font-bold text-black mb-6 leading-relaxed">
                Don't panic! This happens sometimes. We've automatically reported this issue to our team. 
                Here are some quick fixes you can try:
              </p>
              
              {/* Quick Fix Suggestions */}
              <div className="grid gap-4 mb-8">
                <div className="bg-[#1e74e6] border-2 border-black rounded p-4 -rotate-1">
                  <h3 className="font-black text-white text-lg mb-2 uppercase">
                    💡 Quick Fixes
                  </h3>
                  <ul className="space-y-2 text-white font-bold">
                    <li>• Try refreshing the page (Ctrl + Shift + R)</li>
                    <li>• Go back to the previous page (Esc key)</li>
                    <li>• Return to homepage (Ctrl + H)</li>
                    <li>• Clear your browser cache</li>
                  </ul>
                </div>
                
                <div className="bg-[#17cbbf] border-2 border-black rounded p-4 rotate-1">
                  <h3 className="font-black text-black text-lg mb-2 uppercase">
                    ⚡ Keyboard Shortcuts
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-black font-bold text-sm">
                    <div className="bg-white border-2 border-black rounded px-3 py-1">
                      <kbd className="font-black">Ctrl + Shift + R</kbd> - Hard Refresh
                    </div>
                    <div className="bg-white border-2 border-black rounded px-3 py-1">
                      <kbd className="font-black">Esc</kbd> - Go Back
                    </div>
                    <div className="bg-white border-2 border-black rounded px-3 py-1">
                      <kbd className="font-black">Ctrl + H</kbd> - Homepage
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <Button
                onClick={() => window.location.reload()}
                className="bg-black text-white border-4 border-black rounded font-black text-lg py-6 hover:bg-white hover:text-black rotate-2 hover:rotate-0 uppercase"
              >
                <RefreshCw className="mr-2 h-5 w-5" />
                Refresh
              </Button>
              
              <Button
                onClick={() => window.history.back()}
                className="bg-[#1e74e6] text-white border-4 border-black rounded font-black text-lg py-6 hover:bg-black hover:text-white -rotate-1 hover:rotate-0 uppercase"
              >
                <ArrowLeft className="mr-2 h-5 w-5" />
                Go Back
              </Button>
              
              <Button
                onClick={() => window.location.href = '/'}
                className="bg-[#17cbbf] text-black border-4 border-black rounded font-black text-lg py-6 hover:bg-black hover:text-white rotate-1 hover:rotate-0 uppercase"
              >
                <Home className="mr-2 h-5 w-5" />
                Home
              </Button>
              
              <Button
                onClick={() => this.setState({ showFeedback: !this.state.showFeedback })}
                className="bg-[#60a5fa] text-black border-4 border-black rounded font-black text-lg py-6 hover:bg-black hover:text-white -rotate-2 hover:rotate-0 uppercase"
              >
                <MessageSquare className="mr-2 h-5 w-5" />
                Feedback
              </Button>
            </div>

            {/* Feedback Form */}
            {this.state.showFeedback && (
              <div className="bg-white border-4 border-black rounded-lg p-6 rotate-1 mb-6">
                <h3 className="text-2xl font-black text-black mb-4 uppercase">
                  Tell us what happened
                </h3>
                <textarea
                  value={this.state.userFeedback}
                  onChange={(e) => this.setState({ userFeedback: e.target.value })}
                  placeholder="What were you trying to do when this happened?"
                  className="w-full border-4 border-black rounded p-4 font-bold text-black min-h-[120px] resize-none focus:bg-[#facc15] focus:outline-none focus:ring-4 focus:ring-black focus:ring-offset-2"
                />
                <div className="flex gap-2 mt-4">
                  <Button
                    onClick={this.handleFeedbackSubmit}
                    disabled={!this.state.userFeedback.trim()}
                    className="bg-black text-white border-4 border-black rounded font-black py-3 hover:bg-[#facc15] hover:text-black uppercase disabled:opacity-50"
                  >
                    <Zap className="mr-2 h-4 w-4" />
                    Send Feedback
                  </Button>
                  <Button
                    onClick={() => this.setState({ showFeedback: false })}
                    className="bg-white text-black border-4 border-black rounded font-black py-3 hover:bg-black hover:text-white uppercase"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {/* Developer Tools */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <div className="bg-red-400 border-4 border-black rounded-lg p-6 -rotate-1">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-black text-black uppercase">
                    🔧 Developer Info
                  </h3>
                  <Button
                    onClick={this.handleCopyError}
                    className="bg-black text-white border-2 border-black rounded font-bold py-2 px-4 hover:bg-white hover:text-black"
                  >
                    {this.state.copySuccess ? (
                      <>✓ Copied!</>
                    ) : (
                      <>
                        <Copy className="mr-2 h-4 w-4" />
                        Copy Error
                      </>
                    )}
                  </Button>
                </div>
                <details className="bg-black border-2 border-black rounded p-4">
                  <summary className="cursor-pointer font-black text-white mb-2">
                    Error Stack Trace
                  </summary>
                  <pre className="text-xs text-green-400 overflow-auto whitespace-pre-wrap font-mono">
                    {this.state.error.stack}
                  </pre>
                </details>
                {this.state.errorInfo?.componentStack && (
                  <details className="bg-black border-2 border-black rounded p-4 mt-2">
                    <summary className="cursor-pointer font-black text-white mb-2">
                      Component Stack
                    </summary>
                    <pre className="text-xs text-yellow-400 overflow-auto whitespace-pre-wrap font-mono">
                      {this.state.errorInfo.componentStack}
                    </pre>
                  </details>
                )}
              </div>
            )}

            {/* Try Again Button */}
            <div className="text-center mt-6">
              <Button
                onClick={() => this.setState({ hasError: false, error: undefined, errorInfo: undefined, userFeedback: '', showFeedback: false })}
                className="bg-[#facc15] text-black border-4 border-black rounded-lg font-black text-xl py-4 px-8 hover:bg-black hover:text-white rotate-2 hover:rotate-0 uppercase tracking-wide"
              >
                <RotateCcw className="mr-3 h-6 w-6" />
                Try Again
              </Button>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
} 